/**
 * **One occurrence, in full, over another screen.** Owner, 17 September 2026: „widok klient →
 * Ankiety ma podgląd wypełnionej ankiety; chcę go reużyć tutaj — jak ktoś kliknie Podgląd, to
 * podejrzy w DOKŁADNIE tym samym widoku ankietę, w modalu."
 *
 * ── It draws `SurveyReport`, and that is the whole requirement ───────────────────────────────
 * Not a second rendering of a submission, not a projection of one assignment into a lonely list of
 * answers — the same component the client detail's Ankiety pane draws, fed from the same read, with
 * the same deltas against the same previous ANSWERED occurrence. That is why this dialog reads
 * `GET /customer/{id}/surveys` rather than `GET /survey/assignment/{id}`: the assignment route
 * carries one occurrence and has no previous one to subtract, so a report built from it would be
 * the client detail's report minus every comparison on it. `/raporty` and the client detail share
 * ONE cache entry for that read (`api/surveyHistoryQueries`), so opening this dialog on a client
 * whose tab is already loaded costs no request at all.
 *
 * ── The occurrence is CHECKED, never fallen back to ──────────────────────────────────────────
 * `buildSurveyHistory` resolves an unknown id leniently — see `model/reportSubject`, which is where
 * that decision is made and tested. A dialog opened on a named submission that is not in the
 * client's history says so; it does not put a different week on screen under the same title.
 *
 * ── The shell ────────────────────────────────────────────────────────────────────────────────
 * Base UI `Dialog`, FULLY modal rather than `modal="trap-focus"`: there is a scrim over the page,
 * so nothing behind it is meant to be read, and full modality is what marks the rest of the
 * document hidden to assistive technology as well as trapping focus. `DayPreviewDock` takes
 * `trap-focus` for the opposite reason — the calendar behind it stays readable.
 *
 * `Dialog.Root` is mounted only while the dialog is open, which is `DayPreviewDock`'s pattern and
 * is deliberate here: a popup rendered conditionally INSIDE an open portal strands its backdrop —
 * a full-page scrim nothing can click — for the length of the exit transition. The cost is that
 * there is no exit animation, only the entrance Base UI's `[data-starting-style]` gives it.
 *
 * `initialFocus={popup}` for `DayPreviewDock`'s measured reason: the dialog opens on a LOADING
 * state, so the element carrying the title when Base UI resolves initial focus is a placeholder
 * that unmounts the instant the report arrives and takes focus to `<body>` with it. The popup is
 * the one element that exists for the whole opening, and `aria-labelledby` is what a screen reader
 * reads on arrival either way.
 *
 * `finalFocus` is a FUNCTION rather than a ref, resolved at close time, because ‹ › change which
 * occurrence is on screen and marking one reviewed removes the control that opened the dialog. The
 * caller answers "what still exists to give focus back to" when the question is asked.
 *
 * ── „Przejrzana" is INJECTED, and one half of it is not ──────────────────────────────────────
 * A feature may not import another feature, so `features/survey-review`'s hook is mounted by the
 * HOST page and arrives here as `review`. That is the honest seam anyway: which of the host's own
 * lists a mark invalidates is the host's knowledge, not this dialog's. What the dialog does NOT
 * leave to the host is the client's survey history — the report on screen is built from it, and a
 * host that forgot it would leave the tick standing on a submission it had just marked.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DoneMarkIcon, InboxMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import icon from '~/shared/ui/iconButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { clientSurveysQueryKey } from '../api/surveyHistoryQueries';
import { useSurveyHistorySource } from '../lib/useSurveyHistorySource';
import { reportOf } from '../model/reportSubject';
import { buildSurveyHistory, type SurveyColumnView } from '../model/surveyHistory';
import { MarkFailedAlert, MarkReviewedButton } from './SurveyActions';
import { CloseIcon, NextIcon, PreviousIcon } from './SurveyIcons';
import { SurveyReport } from './SurveyReport';
import styles from './SurveyReportDialog.module.css';

const KEY = 'web:surveyReport';

/** One submission, and who answered it. */
export interface SurveyReportOccurrence {
  readonly customerId: number;
  /** `SurveyAssignment.Id` — what every survey surface addresses an occurrence by. */
  readonly assignmentId: number;
  /**
   * Who answered. The surveys read names no person — it is already scoped to one client — so the
   * caller supplies the name it already has. `null` prints the form and the date alone.
   */
  readonly clientName: string | null;
}

/**
 * What the dialog is open ON.
 *
 * `none` is a real subject rather than "closed": pressing „Podgląd" on a form nobody has filled in
 * yet is an answered question, and the answer is a sentence in the app's voice inside the dialog
 * the trainer just opened. Refusing to open would look like a broken button.
 */
export type SurveyReportRequest =
  | ({ readonly kind: 'occurrence' } & SurveyReportOccurrence)
  | { readonly kind: 'none'; readonly formTitle: string };

/**
 * „Przejrzana", as the host mounts it — `features/survey-review`'s own state, structurally. It is a
 * parameter rather than an import because a feature may not import a feature; see the header.
 */
export interface SurveyReportReview {
  readonly markReviewed: (assignmentId: number, onMarked?: () => void) => void;
  readonly marking: boolean;
  readonly markFailed: boolean;
}

export interface SurveyReportDialogProps {
  /** `null` is closed. */
  readonly request: SurveyReportRequest | null;
  readonly onClose: () => void;
  /** The mark, from the host. `null` draws no tick — a surface that cannot write does not offer to. */
  readonly review?: SurveyReportReview | null;
  /** Runs when the mark lands, before the refetch removes anything. Move focus, publish a sentence. */
  readonly onMarked?: (occurrence: SurveyReportOccurrence) => void;
  /** Where focus goes on close, resolved at close time. See the header. */
  readonly finalFocus?: () => HTMLElement | null;
}

export function SurveyReportDialog({
  request,
  onClose,
  review = null,
  onMarked,
  finalFocus,
}: SurveyReportDialogProps) {
  if (request === null) return null;
  return (
    <DialogShell
      // Keyed on the SUBJECT, so opening the dialog on a different submission without closing it
      // discards the step the trainer had walked to. Nothing focusable survives the remount that
      // the trainer could have been on: the popup itself is what holds focus on arrival.
      key={request.kind === 'none' ? 'none' : String(request.assignmentId)}
      request={request}
      onClose={onClose}
      review={review}
      onMarked={onMarked}
      finalFocus={finalFocus}
    />
  );
}

function DialogShell({
  request,
  onClose,
  review,
  onMarked,
  finalFocus,
}: SurveyReportDialogProps & { readonly request: SurveyReportRequest }) {
  const headingId = useId();
  const popup = useRef<HTMLDivElement>(null);

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup
          ref={popup}
          className={styles.popup}
          aria-labelledby={headingId}
          initialFocus={popup}
          finalFocus={finalFocus === undefined ? undefined : () => finalFocus() ?? true}
        >
          {request.kind === 'none' ? (
            <NoSubmission formTitle={request.formTitle} headingId={headingId} onClose={onClose} />
          ) : (
            <ReportBody
              occurrence={request}
              headingId={headingId}
              onClose={onClose}
              review={review ?? null}
              onMarked={onMarked}
            />
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The head every state of this dialog wears, so the popup never changes shape once it is open. */
function Head({
  headingId,
  title,
  subtitle,
  onClose,
  children,
}: {
  readonly headingId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly onClose: () => void;
  readonly children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.head}>
      <div className={styles.headText}>
        <Dialog.Title id={headingId} className={styles.title}>
          {title}
        </Dialog.Title>
        <Dialog.Description className={styles.subtitle}>{subtitle}</Dialog.Description>
      </div>

      <div className={styles.actions}>
        {children}
        <button
          type="button"
          className={`${String(icon.iconButton)} ${String(styles.action)} ${String(styles.closeAction)}`}
          aria-label={t(`${KEY}.close`)}
          title={t(`${KEY}.close`)}
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}

/** „Podgląd" on a form nobody has filled in yet. An answered question, not a refusal. */
function NoSubmission({
  formTitle,
  headingId,
  onClose,
}: {
  readonly formTitle: string;
  readonly headingId: string;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Head
        headingId={headingId}
        title={formTitle}
        subtitle={t(`${KEY}.noSubmissionMeta`)}
        onClose={onClose}
      />
      <div className={styles.body}>
        <div className={styles.state}>
          {/* Nothing waiting is not a fault, so the tone is positive and the sentence says what
            * WILL happen rather than what is missing. */}
          <EmptyState
            mark={<DoneMarkIcon />}
            tone="positive"
            title={t(`${KEY}.noSubmissionTitle`)}
            hint={t(`${KEY}.noSubmissionHint`)}
          />
        </div>
      </div>
    </>
  );
}

function ReportBody({
  occurrence,
  headingId,
  onClose,
  review,
  onMarked,
}: {
  readonly occurrence: SurveyReportOccurrence;
  readonly headingId: string;
  readonly onClose: () => void;
  readonly review: SurveyReportReview | null;
  readonly onMarked?: (occurrence: SurveyReportOccurrence) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  /** Which occurrence of this client's form is on screen. ‹ › move it; `null` is the one opened on. */
  const [stepId, setStepId] = useState<number | null>(null);
  const shown = stepId ?? occurrence.assignmentId;

  const { source, exerciseNames, pending, failed } = useSurveyHistorySource(occurrence.customerId);
  // The whole range, because this dialog draws no chart: `chartRange` narrows the SERIES and
  // nothing the report reads. Written as the widest possible window rather than as a live date, so
  // the build does not depend on the clock.
  const view = buildSurveyHistory(source, {
    t,
    locale: i18n.language,
    chartRange: { from: '0001-01-01', to: '9999-12-31' },
    occurrenceId: shown,
    exerciseNames,
  });
  const subject = reportOf(view, shown);
  const gate = useSkeletonGate(pending);

  // Everything below follows the GATE rather than the read, so the dialog hands over as one box.
  // The gate keeps the bars for up to 300ms after the answer lands, and a head that filled in its
  // subtitle, lit both arrows and offered „przejrzane" over a body still drawn as bars would be the
  // half-finished frame the gate exists to prevent.
  const index = gate.waiting ? -1 : view.columns.findIndex((column) => column.id === shown);
  const previous = index < 0 ? null : (view.columns[index - 1] ?? null);
  const next = index < 0 ? null : (view.columns[index + 1] ?? null);
  const column = !gate.waiting && subject.status === 'ready' ? subject.column : null;

  return (
    <>
      <Head
        headingId={headingId}
        title={
          occurrence.clientName === null
            ? (view.activeForm?.title ?? t(`${KEY}.fallbackTitle`))
            : t(`${KEY}.title`, {
                client: occurrence.clientName,
                form: view.activeForm?.title ?? t(`${KEY}.fallbackTitle`),
              })
        }
        subtitle={
          column === null
            ? t(`${KEY}.subtitlePending`)
            : t(`${KEY}.subtitle`, {
                at: column.ordinalLabel,
                when: column.sentLabel ?? column.dateLabel,
                status: column.statusLabel,
              })
        }
        onClose={onClose}
      >
        <MarkFailedAlert failed={review?.markFailed === true} message={t(`${KEY}.markFailed`)} />
        {/* Both arrows stay in the tab order when refused, so the reason attached to them is
          * reachable at all — the rule every refused control in this app is written against. */}
        <StepAction
          column={previous}
          label={t(`${KEY}.previousOccurrence`)}
          refused={t(`${KEY}.noPreviousOccurrence`)}
          onStep={setStepId}
          glyph={<PreviousIcon />}
        />
        <StepAction
          column={next}
          label={t(`${KEY}.nextOccurrence`)}
          refused={t(`${KEY}.noNextOccurrence`)}
          onStep={setStepId}
          glyph={<NextIcon />}
        />
        {/* Write-once: the control goes the moment `reviewedAt` is set, and no „odznacz" exists. */}
        {review === null || column === null || column.reviewed ? null : (
          <MarkReviewedButton
            className={String(styles.markAction)}
            label={t(`${KEY}.markReviewed`, { at: column.ordinalLabel })}
            marking={review.marking}
            onMark={() => {
              review.markReviewed(column.id, () => {
                // The report on screen is built from this read, so the tick has to go whoever the
                // host is. Its own lists are its own business — see the header.
                void queryClient.invalidateQueries({
                  queryKey: clientSurveysQueryKey(occurrence.customerId),
                });
                onMarked?.({ ...occurrence, assignmentId: column.id });
              });
            }}
          />
        )}
      </Head>

      {/* The reserve is keyed on the same verdict the fork below is. Keyed on the read instead, it
        * would be dropped the moment the answer landed and the box would collapse under the bars
        * the gate is still holding. */}
      <div
        className={`${String(styles.body)} ${column === null ? String(styles.bodyReserve) : ''}`}
      >
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t(`${KEY}.loading`)}</span>
            {gate.skeleton ? (
              <span className={styles.skelBody}>
                <i className={`${String(skel.bar)} ${String(styles.skelHead)}`} />
                {[0, 1, 2, 3].map((block) => (
                  <i key={block} className={`${String(skel.bar)} ${String(styles.skelBlock)}`} />
                ))}
              </span>
            ) : null}
          </>
        ) : failed ? (
          <div className={styles.state}>
            <EmptyState
              mark={<NoteMarkIcon />}
              tone="neutral"
              title={t(`${KEY}.failedTitle`)}
              hint={t(`${KEY}.failedHint`)}
            />
          </div>
        ) : subject.status === 'missing' ? (
          <div className={styles.state}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={t(`${KEY}.missingTitle`)}
              hint={t(`${KEY}.missingHint`)}
            />
          </div>
        ) : (
          <div className={`${String(styles.report)} ${gate.reveal}`}>
            <SurveyReport view={view} />
          </div>
        )}
      </div>
    </>
  );
}

/**
 * One of the two arrows through this client's occurrences of the form.
 *
 * `aria-disabled` rather than the attribute, because a refused control carrying a reason has to
 * stay in the tab order for the reason to be reachable.
 */
function StepAction({
  column,
  label,
  refused,
  onStep,
  glyph,
}: {
  readonly column: SurveyColumnView | null;
  readonly label: string;
  readonly refused: string;
  readonly onStep: (columnId: number) => void;
  readonly glyph: React.ReactNode;
}) {
  const name = column === null ? refused : `${label} — ${column.ordinalLabel}`;
  return (
    <button
      type="button"
      className={`${String(icon.iconButton)} ${String(styles.action)}`}
      aria-disabled={column === null}
      aria-label={name}
      title={name}
      onClick={() => {
        if (column !== null) onStep(column.id);
      }}
    >
      {glyph}
    </button>
  );
}
